/**
 * userUtils.ts — Centralized helpers for user identity fields.
 *
 * ============================================================
 * 🏳️  ORIGIN vs RESIDENCE — DO NOT CONFUSE THESE
 * ============================================================
 *
 *  ORIGIN  (heritage / where you're from)
 *    DB fields : `country` (name)  +  `countryFlag` (code, e.g. "ss" for South Sudan)
 *    Shown as  : the flag emoji/image on every yap, profile, comment, etc.
 *    Set by    : "Country of Origin" picker in Edit Profile / All Flags screen
 *    Rule      : NEVER overwrite this with where the user lives today.
 *
 *  RESIDENCE  (where you live right now)
 *    DB field  : `home_residence` (free-text, e.g. "London, United Kingdom")
 *    Shown as  : the location text underneath a yap ("📍 London, United Kingdom")
 *    Set by    : "Where You Live" input in Edit Profile
 *    Rule      : NEVER use this to determine the flag.
 *
 * ============================================================
 * Always use the helpers below instead of accessing raw fields.
 * ============================================================
 */

/**
 * ============================================================
 * 🖼️  PROFILE PICTURE — one resolution order, everywhere
 * ============================================================
 *
 * A user's photos live in two parallel arrays plus an index:
 *
 *   portfolio[i]          the ORIGINAL uploaded image, uncropped
 *   portfolioPreviews[i]  the user's own SQUARE CROP of that image
 *   profilePictureIndex   which slot they chose as their profile picture
 *   croppedAvatar         a stored copy of portfolioPreviews[profilePictureIndex]
 *   avatar                a denormalized copy of the same, copied onto yaps,
 *                         comments and shouts so a feed needs no user lookup
 *
 * The crop is the point. Showing portfolio[i] where portfolioPreviews[i] exists
 * displays a differently-framed photo than the one the user set — the
 * "zoomed-in chest shot" this app already had a patch for in
 * UserService.updateUserProfile.
 *
 * This was resolved ad hoc in ~35 places, each subtly different: several
 * hardcoded portfolioPreviews[0] and so showed the WRONG PHOTO for anyone whose
 * profile picture is not their first; one preferred the uncropped original
 * outright. Same person, different face, different framing, depending on screen.
 *
 * Use these helpers rather than reaching for the raw fields.
 */

interface AvatarSource {
  portfolio?: (string | null | undefined)[] | null;
  portfolioPreviews?: (string | null | undefined)[] | null;
  profilePictureIndex?: number | null;
  croppedAvatar?: string | null;
  croppedImage?: string | null;
  avatar?: string | null;
  image?: string | null;
}

/**
 * Placeholder URLs that must never be shown as somebody's face.
 *
 * These are the "ghost" profile pictures. Three separate sources, all written at
 * signup and all still sitting in live user documents:
 *
 *   i.pravatar.cc          a third-party random-face service. The Apple signup
 *                          path wrote `?img=12` for every account that arrived
 *                          without a photo, so a real stranger's portrait was
 *                          served as those users' avatar — the same face on all
 *                          of them. Three components also fell back to it at
 *                          render time.
 *   /anonymous-avatar.png  the Google signup fallback, and this file DOES NOT
 *                          EXIST. Only the -blue and -orange variants were ever
 *                          shipped, so this path has always 404'd.
 *   .../a/default-user     Google's own grey silhouette, handed over as
 *                          photoURL for a Google account with no picture set.
 *                          Literally a ghost.
 *
 * Filtering here rather than only at the write sites is what repairs the
 * accounts that already carry these values: every surface resolves through
 * getUserAvatar now, so a ghost stops being displayed immediately, with no data
 * migration. The stored field stays as it is and is simply ignored.
 */
const GHOST_AVATAR_PATTERNS = [
  'i.pravatar.cc',
  '/anonymous-avatar.png',
  'googleusercontent.com/a/default-user',
];

/** True when a URL is a placeholder standing in for a face, not a face. */
export function isGhostAvatar(url?: string | null): boolean {
  if (!url) return true;
  const v = String(url).trim();
  if (!v) return true;
  const lower = v.toLowerCase();
  return GHOST_AVATAR_PATTERNS.some((p) => lower.includes(p));
}

/** A usable, non-ghost value, or ''. */
const usable = (v?: string | null): string =>
  (typeof v === 'string' && v.trim() !== '' && !isGhostAvatar(v)) ? v : '';

const firstUsable = (arr?: (string | null | undefined)[] | null): string =>
  (arr || []).map((v) => usable(v as any)).find((v) => v !== '') || '';

/**
 * The image to show for a user, framed the way they set it.
 *
 * Order, most to least faithful:
 *   1. their crop of their chosen photo
 *   2. croppedAvatar / croppedImage — stored copies of that same crop
 *   3. avatar / image — denormalized copies, which can be stale
 *   4. the uncropped original of their chosen photo
 *   5. any photo they have at all, rather than nothing
 *
 * Steps 4 and 5 knowingly trade correct framing for showing a face instead of a
 * placeholder letter. Everything above them preserves the user's own crop.
 */
export function getUserAvatar(user?: AvatarSource | null): string {
  if (!user) return '';

  const idx = typeof user.profilePictureIndex === 'number' ? user.profilePictureIndex : 0;
  const previews = user.portfolioPreviews || [];
  const originals = user.portfolio || [];

  const chosenCrop = usable(previews[idx] as any);
  if (chosenCrop) return chosenCrop;

  const stored = usable(user.croppedAvatar)
    || usable(user.croppedImage)
    || usable(user.avatar)
    || usable(user.image);
  if (stored) return stored;

  const chosenOriginal = usable(originals[idx] as any);
  if (chosenOriginal) return chosenOriginal;

  return firstUsable(previews) || firstUsable(originals);
}

/** Returns the origin country flag code (e.g. "ss", "gh", "soulaan"). */
export function getUserOriginFlag(user: {
  countryFlag?: string | null;
  country?: string | null;
}): string {
  return user.countryFlag || user.country || '';
}

/** Returns the origin country name (e.g. "South Sudan", "Ghana"). */
export function getUserOriginCountry(user: {
  country?: string | null;
  countryFlag?: string | null;
}): string {
  return user.country || '';
}

/**
 * Returns the location TEXT to display on yaps/profile
 * (home_residence → location → origin country, in order of priority).
 *
 * If `showLocation` is false, returns 'Earth'.
 */
export function getUserLocationText(user: {
  home_residence?: string | null;
  location?: string | null;
  country?: string | null;
  showLocation?: boolean | null;
}): string {
  if (user.showLocation === false) return 'Earth';
  if (user.home_residence && user.home_residence.trim().length >= 2) return user.home_residence;
  if (user.location && user.location.trim().length >= 2) return user.location;
  if (user.country && user.country.trim().length >= 2) return user.country;
  return 'Earth';
}

/**
 * Builds the subset of fields needed when writing a yap/shout document.
 * Guarantees the origin flag and residence text are never swapped.
 */
export function buildYapAuthorFields(user: {
  name?: string;
  username?: string;
  countryFlag?: string | null;
  country?: string | null;
  home_residence?: string | null;
  location?: string | null;
  showLocation?: boolean | null;
  bannerImage?: string | null;
  coverImage?: string | null;
}) {
  return {
    // 🏳️ ORIGIN — always the heritage flag code
    authorCountryFlag: getUserOriginFlag(user),
    country: getUserOriginCountry(user),

    // 📍 RESIDENCE — always the location text
    location: getUserLocationText(user),
    home_residence: user.home_residence || '',
    showLocation: user.showLocation ?? true,
  };
}
